/**
 * JoinPage - Student entry point to join a game session
 * Students enter a join code and their display name to join a session
 */

import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { sessionsService } from '@/services/sessions';

interface JoinFormData {
  code: string;
  displayName: string;
  fullName?: string;
}

export function JoinPage() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState<JoinFormData>({
    code: '',
    displayName: '',
    fullName: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);

  function handleCodeChange(value: string) {
    const normalized = value.toUpperCase().slice(0, 6);
    setFormData((prev) => ({ ...prev, code: normalized }));

    if (normalized.length === 0) {
      setCodeError(null);
    } else if (normalized.length < 4) {
      setCodeError('Join code must be at least 4 characters');
    } else if (!/^[A-Z0-9]+$/.test(normalized)) {
      setCodeError('Join code must contain only letters and numbers');
    } else {
      setCodeError(null);
    }
  }

  function handleDisplayNameChange(value: string) {
    setFormData((prev) => ({ ...prev, displayName: value }));

    if (value.length === 0) {
      setNameError(null);
    } else if (value.trim().length === 0) {
      setNameError('Display name cannot be empty');
    } else if (value.length > 50) {
      setNameError('Display name must be 50 characters or less');
    } else {
      setNameError(null);
    }
  }

  function handleFullNameChange(value: string) {
    setFormData((prev) => ({ ...prev, fullName: value }));
  }

  function isFormValid(): boolean {
    return (
      formData.code.length >= 4 &&
      formData.code.length <= 6 &&
      /^[A-Z0-9]+$/.test(formData.code) &&
      formData.displayName.trim().length > 0 &&
      formData.displayName.length <= 50 &&
      !codeError &&
      !nameError
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();

    if (!isFormValid()) {
      setError('Please fix the errors in the form');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await sessionsService.joinSession({
        code: formData.code,
        displayName: formData.displayName.trim(),
        fullName: formData.fullName?.trim() || undefined,
      });

      sessionStorage.setItem('studentSessionId', response.sessionId);
      sessionStorage.setItem('studentPlayerId', response.playerId);

      navigate(`/student/waiting/${response.sessionId}`);
    } catch (err: unknown) {
      console.error('Failed to join session:', err);
      const apiMessage =
        typeof err === 'object' &&
        err !== null &&
        'response' in err &&
        typeof (err as { response?: unknown }).response === 'object' &&
        (err as { response?: { data?: unknown } }).response?.data &&
        typeof (err as { response?: { data?: { message?: unknown } } }).response?.data?.message === 'string'
          ? (err as { response: { data: { message: string } } }).response.data.message
          : null;
      setError(
        apiMessage ||
          'Failed to join session. Please check your join code and try again.'
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-[#E2E5EC] via-white to-[#CED4DE] px-4 py-10 sm:px-6">
      <div className="pointer-events-none absolute -left-20 top-20 h-56 w-56 rounded-full bg-[#12356B]/10 blur-2xl" />
      <div className="pointer-events-none absolute -right-16 bottom-16 h-56 w-56 rounded-full bg-[#DD6832]/10 blur-2xl" />

      <div className="mx-auto w-full max-w-5xl">
        <div className="mb-8 flex items-center justify-between">
          <img
            src="/icons/centauri-logo.png"
            alt="Centauri"
            className="h-16 object-contain sm:h-20"
          />
          <button
            type="button"
            onClick={() => navigate('/')}
            className="rounded-lg border-2 border-[#12356B] bg-white px-4 py-2 text-sm font-semibold text-[#12356B] transition-colors hover:bg-[#E2E5EC]"
          >
            Back to Home
          </button>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.1fr_1fr]">
          <section className="relative overflow-hidden rounded-2xl border border-[#12356B]/25 bg-[#101822] p-6 text-white shadow-xl sm:p-8">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(54,122,37,0.35),_transparent_55%)]" />
            <div className="relative z-10 space-y-6">
              <div className="inline-flex items-center gap-2.5 rounded-full border border-[#367A25]/40 bg-[#101822] px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-[#367A25]">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#367A25] opacity-75" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#367A25]" />
                </span>
                Mission Status: <span className="ml-0.5 text-white">Active</span>
              </div>

              <div>
                <h1 className="text-3xl font-bold leading-tight text-white sm:text-4xl">
                  Student Entry
                  <span className="block italic text-[#DD6832]">Join the Crew</span>
                </h1>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-300 sm:text-base">
                  Enter your join code and callsign to connect with your team before deployment.
                </p>
              </div>

              <div className="rounded-xl border border-white/20 bg-white/10 p-4 text-sm tracking-wide text-slate-100">
                <div className="flex items-center justify-between border-b border-white/10 pb-2">
                  <span className="font-semibold uppercase text-white">Sector</span>
                  <span className="font-semibold text-[#DD6832]">Bloomtar Prime</span>
                </div>
                <div className="mt-2 flex items-center justify-between border-b border-white/10 pb-2">
                  <span className="font-semibold uppercase text-white">Team Sync</span>
                  <span className="font-semibold text-[#367A25]">Awaiting Players</span>
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <span className="font-semibold uppercase text-white">Protocol</span>
                  <span className="font-semibold text-slate-100">Secure Link</span>
                </div>
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-[#12356B]/30 bg-white p-6 shadow-xl sm:p-8">
            <h2 className="text-2xl font-bold text-[#12356B]">Join a Session</h2>
            <p className="mt-2 mb-6 text-sm text-slate-600 sm:text-base">
              Enter the code shared by your teacher to begin the simulation.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="code" className="mb-2 block text-sm font-semibold text-[#12356B]">
                  Join Code <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  id="code"
                  className={`w-full rounded-lg border bg-white px-4 py-3 text-center font-sans text-2xl font-bold uppercase tracking-[0.12em] text-[#12356B] transition-all duration-200 focus:outline-none focus:ring-4 ${
                    codeError
                      ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
                      : 'border-[#CED4DE] focus:border-[#12356B] focus:ring-[#12356B]/15'
                  }`}
                  value={formData.code}
                  onChange={(e) => handleCodeChange(e.target.value)}
                  placeholder="ABCD"
                  autoComplete="off"
                  autoFocus
                  disabled={loading}
                />
                {codeError && <p className="mt-1 text-sm text-red-600">{codeError}</p>}
                <p className="mt-1 text-sm text-slate-500">
                  4-6 letters or numbers
                </p>
              </div>

              <div>
                <label htmlFor="displayName" className="mb-2 block text-sm font-semibold text-[#12356B]">
                  Display Name <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  id="displayName"
                  className={`w-full rounded-lg border bg-white px-4 py-3 text-base text-slate-800 transition-all duration-200 focus:outline-none focus:ring-4 ${
                    nameError
                      ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
                      : 'border-[#CED4DE] focus:border-[#12356B] focus:ring-[#12356B]/15'
                  }`}
                  value={formData.displayName}
                  onChange={(e) => handleDisplayNameChange(e.target.value)}
                  placeholder="Your name or nickname"
                  maxLength={50}
                  disabled={loading}
                />
                {nameError && <p className="mt-1 text-sm text-red-600">{nameError}</p>}
                <p className="mt-1 text-sm text-slate-500">
                  Seen by other players (max 50 chars)
                </p>
              </div>

              <div>
                <label htmlFor="fullName" className="mb-2 block text-sm font-semibold text-[#12356B]">
                  Full Name <span className="font-normal text-slate-500">(Optional)</span>
                </label>
                <input
                  type="text"
                  id="fullName"
                  className="w-full rounded-lg border border-[#CED4DE] bg-white px-4 py-3 text-base text-slate-800 transition-all duration-200 focus:border-[#12356B] focus:outline-none focus:ring-4 focus:ring-[#12356B]/15"
                  value={formData.fullName}
                  onChange={(e) => handleFullNameChange(e.target.value)}
                  placeholder="Your full name (for teacher records)"
                  maxLength={100}
                  disabled={loading}
                />
                <p className="mt-1 text-sm text-slate-500">
                  Used for records and certificates
                </p>
              </div>

              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  <strong className="font-semibold">Error:</strong> {error}
                </div>
              )}

              <button
                type="submit"
                className="w-full rounded-lg bg-[#367A25] px-6 py-3 text-base font-semibold text-white shadow-md transition-all hover:bg-[#2d6820] disabled:cursor-not-allowed disabled:opacity-50"
                disabled={!isFormValid() || loading}
              >
                {loading ? 'Joining...' : 'Join Session'}
              </button>

              <p className="pt-1 text-center text-sm text-slate-500">
                Need a code? Ask your teacher to create a new session.
              </p>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
}
